import React, {
  useEffect,
  memo,
  MouseEventHandler,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { ChevronRight, ArrowLeft, Clear } from '@procore/core-icons/dist';
import {
  Button,
  Switch,
  Tooltip,
  Typography,
  useI18nContext,
} from '@procore/core-react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import Slide from '@mui/material/Slide';
import { Help } from '@mui/icons-material';
import { FilterState } from '../../helpers/filters';
import { useStyles } from './MobileFilters.styles';
import FilterCheckboxes, { FilterCheckboxesProps } from './FilterCheckboxes';
import { FiltersLists } from '../../services/business-registry';
import { FilterPill } from './FiltersPills';
import { getTradesToUpdate } from '../utils/tradesBulkCheckboxUtil';
import { BreadcrumbItem } from '../Breadcrumbs/BreadcrumbContainer';
import AnalyticsWrappedComponent from '../Analytics/AnalyticsWrappedComponent';

const SearchByOfficeAddressToggle = memo(
  ({ value, update }: { value: boolean; update: any }) => {
    const I18n = useI18nContext();
    const classes = useStyles();

    return (
      <div className={classes.onlyOfficeAddressContainer}>
        <AnalyticsWrappedComponent
          componentType={Switch}
          eventText="Clicked Only Office Address Toggle"
          eventLabel="Only Office Address Toggle"
          additionalEventProperties={{
            View: 'mobile',
            Value: value,
          }}
          id="only-office-address"
          checked={value}
          onChange={() => {
            update(!value);
          }}
        />
        <label
          className={classes.onlyOfficeAddressText}
          htmlFor="only-office-address"
          suppressHydrationWarning
        >
          {I18n.t('components.only_office_address.label')}
        </label>
        <Tooltip
          overlay={I18n.t('components.only_office_address.tooltip')}
          trigger="click"
        >
          <Help className={classes.onlyOfficeAddressTooltip} />
        </Tooltip>
      </div>
    );
  }
);

interface FilterSectionOverlayProps extends FilterCheckboxesProps {
  sectionName: string;
  handleCloseOverlay: MouseEventHandler<HTMLDivElement>;
  open: boolean;
  closeFilters: MouseEventHandler<HTMLOrSVGElement>;
}

const FilterSectionOverlay = memo(
  ({
    sectionName,
    updateCheckbox,
    handleCloseOverlay,
    FiltersList,
    selectedFilters,
    dataTrackClick,
    analyticsEventText,
    open,
    closeFilters,
    showSearch,
    sublistPropertyName,
  }: FilterSectionOverlayProps) => {
    const classes = useStyles();
    const [checkboxesVisible, setCheckboxesVisibile] = useState(false);

    useEffect(() => {
      if (open) {
        setCheckboxesVisibile(true);
      }
    }, [open]);

    return (
      <Slide in={open} direction="left" timeout={500}>
        <Box className={classes.sectionContainer}>
          <Box
            className={classes.sectionTitle}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center">
              <Box
                data-test-id="back-button"
                onClick={handleCloseOverlay}
                className={classes.backButton}
                pr={1}
              >
                <ArrowLeft />
              </Box>
              <Typography
                intent="h2"
                data-test-id={`filter-overlay-title-${sectionName}`}
              >
                {sectionName}
              </Typography>
            </Box>
            <Clear className={classes.closeIcon} onClick={closeFilters} />
          </Box>
          <Box className={classes.checkboxContainer}>
            {checkboxesVisible && (
              <FilterCheckboxes
                sectionName={sectionName}
                updateCheckbox={updateCheckbox}
                FiltersList={FiltersList}
                selectedFilters={selectedFilters}
                dataTrackClick={dataTrackClick}
                analyticsEventText={analyticsEventText}
                showSearch={showSearch}
                sublistPropertyName={sublistPropertyName}
              />
            )}
          </Box>
        </Box>
      </Slide>
    );
  }
);

interface FilterSectionProps extends FilterCheckboxesProps {
  sectionName: string;
  closeFilters: MouseEventHandler<HTMLOrSVGElement>;
  setShowPills: Function;
}

const FilterSection = memo(
  ({
    sectionName,
    updateCheckbox,
    FiltersList,
    selectedFilters,
    dataTrackClick,
    analyticsEventText,
    closeFilters,
    showSearch,
    sublistPropertyName,
    setShowPills,
  }: FilterSectionProps) => {
    const [showOverlay, setShowOverlay] = useState(false);
    const classes = useStyles();
    const selectedFiltersCount = selectedFilters.length;

    return (
      <Box data-test-id={`filter-section-overlay-${sectionName}`}>
        <FilterSectionOverlay
          sectionName={sectionName}
          handleCloseOverlay={() => {
            setShowOverlay(false);
            setShowPills(true);
          }}
          updateCheckbox={updateCheckbox}
          FiltersList={FiltersList}
          selectedFilters={selectedFilters}
          dataTrackClick={dataTrackClick}
          analyticsEventText={analyticsEventText}
          open={showOverlay}
          closeFilters={closeFilters}
          showSearch={showSearch}
          sublistPropertyName={sublistPropertyName}
        />
        <Box
          className={classes.filterSection}
          onClick={() => {
            setShowOverlay(!showOverlay);
            setShowPills(false);
          }}
        >
          <Box
            data-test-id="section-title"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography intent="body">
              {selectedFiltersCount > 0
                ? `${sectionName} (${String(selectedFiltersCount)})`
                : `${sectionName}`}
            </Typography>
            <ChevronRight />
          </Box>
        </Box>
      </Box>
    );
  }
);

interface MobileFiltersProps {
  useFilterHandlers: FilterState;
  loading: boolean;
  searchResultsCount: number;
  closeFilters: MouseEventHandler<HTMLButtonElement | HTMLElement | SVGElement>;
  open: boolean;
  filtersLists: FiltersLists;
  breadcrumbs: Array<BreadcrumbItem>;
}

export default memo(function MobileFilters({
  useFilterHandlers,
  loading,
  searchResultsCount,
  closeFilters,
  open,
  filtersLists,
  breadcrumbs,
}: MobileFiltersProps) {
  const classes = useStyles();
  const I18n = useI18nContext();
  const {
    filters,
    updateCheckbox,
    updateCheckboxBulk,
    updateOnlyOfficeAddress,
    clearFilters,
  } = useFilterHandlers;
  const [showPills, setShowPills] = useState(true);
  const selectedFilters = [
    ...filters.constructionSector,
    ...filters.companyType,
    ...filters.classifications,
    ...filters.trade,
  ];
  const filterCount = selectedFilters.length;

  const updateCheckboxMemo = useMemo(() => updateCheckbox, []);
  const updateTradesMemo = useCallback(
    (item) => {
      const { checked, trades } = getTradesToUpdate(
        item,
        filtersLists.trades,
        filters.trade
      );
      updateCheckboxBulk(trades, checked);
    },
    [filtersLists.trades, filters.trade]
  );

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={closeFilters}
      className={classes.drawer}
    >
      <Box
        data-test-id="title-container"
        className={classes.title}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography intent="h2">
          {I18n.t('components.filters.filter')}
        </Typography>
        <Clear className={classes.closeIcon} onClick={closeFilters} />
      </Box>
      <Divider />
      <Box className={classes.filterWrapper}>
        {breadcrumbs.length > 0 && (
          <SearchByOfficeAddressToggle
            value={filters.onlyOfficeAddress}
            update={updateOnlyOfficeAddress}
          />
        )}
        <FilterSection
          sectionName={I18n.t('components.filters.market_sector')}
          updateCheckbox={updateCheckboxMemo}
          FiltersList={filtersLists.constructionSectors}
          selectedFilters={filters.constructionSector}
          dataTrackClick="Construction Sector Filters"
          analyticsEventText="Clicked Market Sector Filter Checkbox"
          closeFilters={closeFilters}
          setShowPills={setShowPills}
        />
        <Divider />
        <FilterSection
          sectionName={I18n.t('components.filters.company_type')}
          updateCheckbox={updateCheckboxMemo}
          FiltersList={filtersLists.companyTypes}
          selectedFilters={filters.companyType}
          dataTrackClick="Company Type Options"
          analyticsEventText="Clicked Company Type Filter Checkbox"
          closeFilters={closeFilters}
          setShowPills={setShowPills}
        />
        <Divider />
        <FilterSection
          sectionName={I18n.t('components.filters.business_classification')}
          updateCheckbox={updateCheckboxMemo}
          FiltersList={filtersLists.classifications}
          selectedFilters={filters.classifications}
          dataTrackClick="Business Classification Options"
          analyticsEventText="Clicked Business Classification Filter Checkbox"
          closeFilters={closeFilters}
          setShowPills={setShowPills}
        />
        <Divider />
        <FilterSection
          sectionName={I18n.t('components.filters.trades_and_services')}
          updateCheckbox={updateTradesMemo}
          FiltersList={filtersLists.trades}
          selectedFilters={filters.trade}
          dataTrackClick="Trade Filters"
          analyticsEventText="Clicked Trade Filter Checkbox"
          closeFilters={closeFilters}
          setShowPills={setShowPills}
          showSearch
          sublistPropertyName="specialties"
        />
        {showPills && (
          <Box
            className={classes.pillsContainer}
            ml={3}
            mt={-2.5}
            data-test-id="pills-container"
          >
            {selectedFilters.map((item) => (
              <Box mb={0.5} key={`${item.type}-${item.key}`}>
                <FilterPill item={item} updateCheckbox={updateCheckbox} />
              </Box>
            ))}
          </Box>
        )}
      </Box>
      <Box className={classes.actionContainer}>
        {filterCount > 0 && (
          <Button
            data-test-id="reset-button"
            onClick={clearFilters}
            loading={loading}
            variant="secondary"
          >
            {I18n.t('components.filters.reset')}
          </Button>
        )}
        <Button
          data-test-id="search-results-button"
          onClick={closeFilters}
          loading={loading}
        >
          {I18n.t(
            `components.filters.show_results_count_${
              searchResultsCount === 1 ? 'singular' : 'plural'
            }`,
            {
              count: searchResultsCount.toLocaleString(),
            }
          )}
        </Button>
      </Box>
    </Drawer>
  );
});
